import React, { useImperativeHandle, useState } from 'react';

import './SongProgress.scss';

export interface SongProgressHandle {
  /** Called from the player's timeupdate event with the element's own values. */
  update: (currentTime: number, duration: number) => void;
}

interface SongProgressProps {
  /** False for the filler or anything else with no end, which leaves the strip empty. */
  visible: boolean;
  ref?: React.Ref<SongProgressHandle>;
}

const formatTime = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

/**
 * A thin progress bar for the song on the venue screen.
 *
 * It sits in the now playing card at the bottom of the screen. It keeps its
 * height when there is nothing to show, so the card does not jump between songs.
 *
 * Position is pushed in through a handle rather than props: timeupdate fires
 * several times a second, and re-rendering PlayerView that often would re-run
 * its skip checks on every tick. Only this component re-renders.
 *
 * Give it a key per song so it starts from zero, not by sliding backwards.
 */
const SongProgress = ({ visible, ref }: SongProgressProps) => {
  const [time, setTime] = useState({ current: 0, duration: 0 });

  useImperativeHandle(ref, () => ({
    update: (current, duration) => setTime({ current, duration }),
  }));

  const { current, duration } = time;
  const known = visible && Number.isFinite(duration) && duration > 0;
  const fraction = known ? Math.min(1, Math.max(0, current / duration)) : 0;

  return (
    <div className="song-progress" aria-hidden={!known}>
      {known && (
        <>
          <span className="song-progress-time">{formatTime(current)}</span>
          <div
            className="song-progress-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
          >
            <div className="song-progress-fill" style={{ transform: `scaleX(${fraction})` }} />
          </div>
          <span className="song-progress-time">{formatTime(duration)}</span>
        </>
      )}
    </div>
  );
};

export default SongProgress;
